import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { getNextPageParamFromPaginatedResult } from '@/api/infiniteQueries';
import { graphql } from '@/gql';
import { GetLineItemsInput, PaginationInput } from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import lineItemKeys from './keyFactory';

const GetLineItems = graphql(`
  query LineItems($pagination: PaginationInput!, $input: GetLineItemsInput) {
    lineItems(pagination: $pagination, input: $input) {
      items {
        id
        publicId
        name
        description
        price {
          taxRate {
            id
            label
          }
          unitPrice {
            value
            currency {
              id
              code
              exponent
            }
          }
          unitPriceExcTax {
            value
            currency {
              id
              code
              exponent
            }
          }
        }
        thumbnailUrl
      }
      limit
      offset
      totalCount
    }
  }
`);

export const useGetInfiniteLineItems = (input?: GetLineItemsInput) => {
  const queryFn = ({ pageParam }: { pageParam: number }) => {
    return gqlClient.request(GetLineItems, {
      pagination: {
        limit: PAGINATION_ARG_DEFAULTS.limit,
        offset: pageParam,
      },
      input,
    });
  };

  return useInfiniteQuery({
    queryKey: lineItemKeys.infiniteLineItems(input),
    queryFn,
    initialPageParam: PAGINATION_ARG_DEFAULTS.offset,
    getNextPageParam: getNextPageParamFromPaginatedResult(
      (lastPage) => lastPage.lineItems
    ),
    placeholderData: keepPreviousData,
  });
};

const useGetLineItems = (
  pagination?: PaginationInput,
  input?: GetLineItemsInput
) => {
  return useQuery({
    queryKey: lineItemKeys.lineItems(pagination, input),
    queryFn: () => {
      return gqlClient.request(GetLineItems, {
        pagination: pagination || PAGINATION_ARG_DEFAULTS,
        input: input,
      });
    },
  });
};

export default useGetLineItems;
